import React, { useState, useEffect } from 'react';
import backIcon from '../../assets/img/back.svg';
import chevronup from '../../assets/img/chevron-up.svg';
import chevrondown from '../../assets/img/chevron-down.svg';
import threedots from '../../assets/img/three-dots-vertical.svg';
import searchimg from '../../assets/img/search.svg';
import downloadimg from '../../assets/img/download.svg';
import * as XLSX from "xlsx";
import { useUser } from "../../context/UserContext";

function SignatureLogDetails({ employee, onBack }) {
  const { role } = useUser();
  const isAdmin = role === "admin";

  const empSig = employee?.logs || [];
  const [decryptedLogs, setDecryptedLogs] = useState([]);
  const [isDecrypting, setIsDecrypting] = useState(true);

  const godrejStyles = { primary: "#810055", secondary: "#707070", font: "GB-BodyCopy" };

  // Backend already returns decrypted data, use directly
  useEffect(() => {
    setDecryptedLogs(empSig);
    setIsDecrypting(false);
  }, [empSig]);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ type: "Contains", value: "" });

  // Column resize state
  const [columnWidths, setColumnWidths] = useState({});
  const resizingCol = React.useRef(null);
  const startX = React.useRef(0);
  const startWidth = React.useRef(0);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [hoveredCol, setHoveredCol] = useState(null);

  const columns = [
    { key: "esl_name", label: "Name" },
    { key: "esl_salary_code", label: "Salary Code" },
    { key: "esl_company_code", label: "Company" },
    { key: "esl_division_code", label: "Division" },
    { key: "esl_address", label: "Address" },
    { key: "esl_telephone_no", label: "Tel" },
    { key: "esl_mobile_no", label: "Mob" },
    { key: "esl_message", label: "Message" },
    { key: "esl_log_created_on", label: "Updated On" },
  ];

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${minutes}`;
  };

  const maskPII = (value) => {
    if (!value || typeof value !== 'string' || value.length < 6) return value;
    const visible = 4;
    return value.slice(0, -visible).replace(/[0-9]/g, '*') + value.slice(-visible);
  };

  const getCellValue = (row, key) => {
    if (key === "esl_log_created_on") return formatDateTime(row[key]);
    if (key === "esl_telephone_no" || key === "esl_mobile_no") {
      return maskPII(row[key]);
    }
    return row[key] || "";
  };

  const updateFilterLive = (colKey, draft) => {
    setFilterDraft(draft);
  };

  const handleMouseDown = (e, colKey) => {
    e.preventDefault();
    resizingCol.current = colKey;
    startX.current = e.clientX;
    const th = e.target.parentElement;
    startWidth.current = th.offsetWidth;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    setColumnWidths(prev => ({ ...prev, [resizingCol.current]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleSort = (colKey) => {
    setSortConfig(prev => ({
      key: colKey,
      direction: prev.key === colKey && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const applyColumnFilter = (colKey) => {
    if (filterDraft.value || filterDraft.type === "Blank" || filterDraft.type === "Not Blank") {
      setColumnFilters(prev => ({ ...prev, [colKey]: { ...filterDraft } }));
    }
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const resetColumnFilter = (colKey) => {
    setColumnFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setFilterDraft({ type: "Contains", value: "" });
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const matchesFilter = (val, filter) => {
    const cellVal = String(val || "").toLowerCase();
    const filterVal = (filter.value || "").toLowerCase();
    switch (filter.type) {
      case "Contains": return cellVal.includes(filterVal);
      case "Not Contains": return !cellVal.includes(filterVal);
      case "Equals": return cellVal === filterVal;
      case "Not Equals": return cellVal !== filterVal;
      case "Starts With": return cellVal.startsWith(filterVal);
      case "Ends With": return cellVal.endsWith(filterVal);
      case "Blank": return cellVal.trim() === "";
      case "Not Blank": return cellVal.trim() !== "";
      default: return true;
    }
  };

  const filteredLogs = decryptedLogs.filter(row => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = columns.some(col => String(getCellValue(row, col.key) || "").toLowerCase().includes(term));
      if (!matches) return false;
    }
    for (const [colKey, filter] of Object.entries(columnFilters)) {
      if (!matchesFilter(getCellValue(row, colKey), filter)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortConfig.key) {
      const aTime = new Date(a.esl_log_created_on || 0).getTime();
      const bTime = new Date(b.esl_log_created_on || 0).getTime();
      return bTime - aTime;
    }
    const aVal = String(getCellValue(a, sortConfig.key) || "").toLowerCase();
    const bVal = String(getCellValue(b, sortConfig.key) || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const numberOfPages = () => Math.ceil(filteredLogs.length / pageSize);

  const handleDownload = () => {
    const exportData = filteredLogs.map(row => ({
      "Name": row.esl_name,
      "Salary Code": row.esl_salary_code,
      "Company": row.esl_company_code,
      "Division": row.esl_division_code,
      "Address": row.esl_address,
      "Tel": isAdmin ? row.esl_telephone_no : maskPII(row.esl_telephone_no),
      "Mob": isAdmin ? row.esl_mobile_no : maskPII(row.esl_mobile_no),
      "Message": row.esl_message,
      "Updated On": formatDateTime(row.esl_log_created_on),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SignatureLog");
    XLSX.writeFile(wb, "SignatureLogData.xlsx");
  };

  return (
    <div style={{ fontFamily: godrejStyles.font, paddingRight: "10px" }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: "10px", paddingRight: "10px", marginBottom: "10px" }}>
        <a style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }} onClick={onBack}>
          <img src={backIcon} alt="back" />
          <span style={{ fontSize: '14px', color: '#810055' }}>Back</span>
        </a>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input 
            type="text" 
            placeholder="Search here..." 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            style={{ 
              width: "250px", 
              height: "35px", 
              padding: "4px 34px", 
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              backgroundImage: `url(${searchimg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "10px center",
              backgroundSize: "16px 16px"
            }} 
          />
          <img 
            src={downloadimg} 
            alt="Download" 
            title="Download" 
            onClick={handleDownload}
            style={{ 
              width: "20px", 
              height: "20px", 
              marginLeft: "15px",
              cursor: "pointer"
            }} 
          />
        </div>
      </div>

      <div>
        {isDecrypting ? (
          <div style={{ padding: "20px", textAlign: "center", color: godrejStyles.secondary }}>
            Decrypting data...
          </div>
        ) : (
        <>
        {Object.keys(columnFilters).length > 0 && (
          <div className="filterList" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '5px 10px' }}>
            {Object.entries(columnFilters).map(([colKey, filter]) => {
              const col = columns.find(c => c.key === colKey);
              return (
                <div key={colKey} className="filter-col pr-0 me-2 mb-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9F2F6', borderRadius: '25px', padding: '2px 8px', fontSize: '13px' }}>
                  <span className="ps-2 btn-text">{col?.label || colKey} : {filter.type === 'Blank' || filter.type === 'Not Blank' ? filter.type : filter.value}</span>
                  <div onClick={() => resetColumnFilter(colKey)} style={{ cursor: 'pointer', marginLeft: '4px' }}>
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path></svg>
                  </div>
                </div>
              );
            })}
            <div>
              <button type="button" className="btn" style={{ backgroundColor: 'white', color: '#810055', border: '1px solid #810055', padding: '3px 15px', borderRadius: '25px', textTransform: 'none', fontSize: '14px', fontWeight: '500', marginLeft: '6px' }} onClick={() => { setColumnFilters({}); setCurrentPage(0); }}>Clear All</button>
            </div>
          </div>
        )}
        <div style={{ maxHeight: "400px", overflowY: "scroll", overflowX: "scroll", padding: "0px 0px 10px 10px", boxSizing: "border-box", backgroundColor: "#fff", marginTop: "5px" }}>
          <table className="table table-hover mb-0" style={{ width: "100%", minWidth: "1400px", borderCollapse: "separate", borderSpacing: "0 3px" }}>
            <thead style={{ backgroundColor: godrejStyles.secondary, color: "#fff", fontSize: "0.9rem", position: "sticky", top: 0, zIndex: 2, verticalAlign: "top", height: "36px", fontWeight: "400", whiteSpace: "nowrap" }}>
              <tr>
                {columns.map(col => (
                  <th key={col.key} onMouseEnter={() => setHoveredCol(col.key)} onMouseLeave={() => setHoveredCol(null)} style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "left", width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : "auto", backgroundColor: godrejStyles.secondary, boxShadow: "0 -4px 0 0 #fff" }}>
                    <span style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      {col.label}
                      <img
                        src={chevronup}
                        alt="sort"
                        onClick={() => handleSort(col.key)}
                        style={{ width: "12px", height: "12px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", transform: sortConfig.key === col.key && sortConfig.direction === "desc" ? "rotate(180deg)" : "none", opacity: hoveredCol === col.key || sortConfig.key === col.key ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                      <img
                        src={threedots}
                        alt="filter"
                        onClick={() => {
                          if (activeFilter === col.key) {
                            setActiveFilter(null);
                          } else {
                            setActiveFilter(col.key);
                            setFilterDraft(columnFilters[col.key] || { type: "Contains", value: "" });
                          }
                        }}
                        style={{ width: "14px", height: "14px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", opacity: hoveredCol === col.key || activeFilter === col.key || columnFilters[col.key] ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                    </span>
                    {activeFilter === col.key && (
                      <div style={{ position: "absolute", top: "36px", left: "0", zIndex: 1000, background: "#fff", border: "1px solid #ccc", borderRadius: "4px", padding: "12px", minWidth: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                        <select
                          className="form-control"
                          value={filterDraft.type}
                          onChange={(e) => updateFilterLive(col.key, { ...filterDraft, type: e.target.value })}
                          style={{ fontSize: "13px", marginBottom: "8px", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: `url(${chevrondown})`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", backgroundSize: "12px 12px", paddingRight: "30px" }}
                        >
                          {["Contains", "Not Contains", "Equals", "Not Equals", "Starts With", "Ends With", "Blank", "Not Blank"].map(opt => (
                            <option key={opt}>{opt}</option>
                          ))}
                        </select>
                        {!["Blank", "Not Blank"].includes(filterDraft.type) && (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Filter..."
                            value={filterDraft.value}
                            onChange={(e) => updateFilterLive(col.key, { ...filterDraft, value: e.target.value })}
                            style={{ fontSize: "13px", marginBottom: "8px" }}
                          />
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                          <span onClick={() => resetColumnFilter(col.key)} style={{ cursor: "pointer", color: "#707070", fontSize: "12px", fontWeight: "600" }}>RESET</span>
                          <span onClick={() => applyColumnFilter(col.key)} style={{ cursor: "pointer", color: "#810055", fontSize: "12px", fontWeight: "600" }}>APPLY</span>
                        </div>
                      </div>
                    )}
                    <div onMouseDown={(e) => handleMouseDown(e, col.key)} style={{ position: "absolute", right: 0, top: "25%", height: "50%", width: "2px", backgroundColor: "#a0a0a0", cursor: "col-resize" }}></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length > 0 ? (
                filteredLogs.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map((es, index) => (
                  <tr key={index} style={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}>
                    {columns.map((col, idx) => (
                      <td key={col.key} style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", ...(idx === 0 ? { borderLeft: "1px solid #e0e0e0" } : {}), ...(idx === columns.length - 1 ? { borderRight: "1px solid #e0e0e0" } : {}) }}>{getCellValue(es, col.key)}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4" style={{ color: godrejStyles.secondary }}>No record found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '20px', backgroundColor: '#fff' }}>
          <div style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#707070' }}>Items per page</span>
              <input 
                min="1" 
                max="10"
                type="number" 
                value={pageSize}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= 10) {
                    setPageSize(val);
                    setCurrentPage(0);
                  }
                }}
                style={{
                  width: '60px',
                  height: '30px',
                  border: 'none',
                  borderBottom: '1px solid #000',
                  borderRadius: '0',
                  textAlign: 'center',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0 || filteredLogs.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage === 0 || filteredLogs.length === 0) ? '#ccc' : '#707070',
                  cursor: (currentPage === 0 || filteredLogs.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"></path>
                </svg>
                Previous
              </button>
              <div style={{ width: '1px', height: '30px', backgroundColor: '#e7e7e7' }}></div>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= numberOfPages() - 1 || filteredLogs.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage >= numberOfPages() - 1 || filteredLogs.length === 0) ? '#ccc' : '#810055',
                  cursor: (currentPage >= numberOfPages() - 1 || filteredLogs.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"></path>
                </svg>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', color: '#707070' }}>You are on Page No.</span>
                <select 
                  value={filteredLogs.length > 0 ? currentPage + 1 : 1}
                  onChange={(e) => setCurrentPage(parseInt(e.target.value) - 1)}
                  disabled={filteredLogs.length === 0}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    minWidth: '60px',
                    backgroundColor: 'transparent',
                    outline: 'none'
                  }}
                >
                  {filteredLogs.length > 0 ? (
                    Array.from({ length: numberOfPages() }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))
                  ) : (
                    <option value={1}>1</option>
                  )}
                </select>
              </div>
              <span style={{ fontSize: '14px', color: '#707070' }}>Total {filteredLogs.length} Records</span>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default SignatureLogDetails;
